import type { CardNames } from '@/components/Card/Card.tsx';
import type { PaymentMethod, PaymentType } from '@/lib/types.ts';

type PaymentTypeLike = PaymentType | string | { slug?: string } | null | undefined;

const ACH_PAYMENT_TYPE_SLUGS = new Set([
  'ach',
  'bank_account',
  'plaid',
  'us_bank_account',
]);

const CARD_PAYMENT_TYPE_SLUGS = new Set([
  'card',
  'card_wallet',
  'credit_card',
  'wallet',
]);

const WIRE_PAYMENT_TYPE_SLUGS = new Set(['wire', 'wire_transfer']);

export const normalizePaymentTypeSlug = (type: PaymentTypeLike): string => {
  if (!type) {
    return '';
  }

  if (typeof type === 'string') {
    return type.toLowerCase();
  }

  return type.slug?.toLowerCase() ?? '';
};

const hasPaymentType = (
  types: PaymentTypeLike[] | undefined,
  acceptedSlugs: Set<string>
): boolean =>
  !!types?.some((type) => acceptedSlugs.has(normalizePaymentTypeSlug(type)));

export const allowsAchPaymentType = (types: PaymentTypeLike[] | undefined): boolean =>
  hasPaymentType(types, ACH_PAYMENT_TYPE_SLUGS);

export const allowsCardWalletPaymentType = (
  types: PaymentTypeLike[] | undefined
): boolean => hasPaymentType(types, CARD_PAYMENT_TYPE_SLUGS);

export const allowsWirePaymentType = (types: PaymentTypeLike[] | undefined): boolean =>
  hasPaymentType(types, WIRE_PAYMENT_TYPE_SLUGS);

export const isBankPaymentMethod = (
  method: Pick<PaymentMethod, 'brand'> | null | undefined
): boolean => {
  const brand = method?.brand?.toLowerCase() ?? '';
  return !brand || brand === 'ach' || brand === 'bank' || brand === 'us_bank_account';
};

export const getPaymentMethodLast4 = (
  method: Pick<PaymentMethod, 'number'> | null | undefined
): string => {
  const raw = method?.number ?? '';
  const digits = raw.replace(/\D/g, '');
  if (digits.length >= 4) {
    return digits.slice(-4);
  }

  return raw || '****';
};

export { ACH_PAYMENT_TYPE_SLUGS, CARD_PAYMENT_TYPE_SLUGS, WIRE_PAYMENT_TYPE_SLUGS };

const PAYMENT_SLUG_LABELS: Record<string, string> = {
  ach: 'ACH',
  card_wallet: 'Card',
  wire: 'Wire',
};

function canonicalize(type: PaymentTypeLike): { slug: string; name: string } | null {
  const raw = normalizePaymentTypeSlug(type);
  if (!raw) return null;
  if (ACH_PAYMENT_TYPE_SLUGS.has(raw)) return { slug: 'ach', name: 'ACH' };
  if (CARD_PAYMENT_TYPE_SLUGS.has(raw)) return { slug: 'card_wallet', name: 'Card' };
  if (WIRE_PAYMENT_TYPE_SLUGS.has(raw)) return { slug: 'wire', name: 'Wire' };
  return { slug: raw, name: raw };
}

export function buildPaymentMethodOptions(
  allowedPaymentTypes: ReadonlyArray<PaymentTypeLike> | null | undefined
): Array<{ value: string; label: string }> {
  const seen = new Set<string>();
  return (allowedPaymentTypes ?? [])
    .map(canonicalize)
    .filter((pt): pt is { slug: string; name: string } => {
      if (!pt || seen.has(pt.slug)) return false;
      seen.add(pt.slug);
      return true;
    })
    .sort((a, b) => a.slug.localeCompare(b.slug))
    .map((pt) => ({ value: pt.slug, label: PAYMENT_SLUG_LABELS[pt.slug] ?? pt.name }));
}

export const getPaymentMethodIconName = (
  method: Pick<PaymentMethod, 'brand'> | null | undefined
): CardNames => {
  const brand = method?.brand?.toLowerCase() ?? '';

  if (isBankPaymentMethod(method)) {
    return 'bank';
  }

  if (brand.includes('amex')) {
    return 'amex';
  }

  if (brand.includes('master')) {
    return 'mastercard';
  }

  if (brand.includes('discover')) {
    return 'discover';
  }

  if (brand.includes('diners')) {
    return 'diners';
  }

  if (brand.includes('apple')) {
    return 'applepay';
  }

  return 'visa';
};
